import { useRef, useState } from "react";
import { Check, FunnelPlus, History, Pin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FILTER_FIELD_GROUPS, type FilterField } from "@/config/filterFields";
import { PINNED_MAX } from "@/lib/filter-prefs";
import { cn } from "@/lib/utils";

const PINNABLE = new Set(FILTER_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key)));

/**
 * "Add filter": the recent filter sets, then the fields in their groups,
 * searchable. A used field is ticked and opens its chip; the pin puts a field
 * in the bar as an empty quick slot (owner, 30.10.2026: the slots are the
 * user's own). The one filled control of the bar, so it does not get lost
 * among the slots (owner, 03.11.2026; docs/clicks-filter-buttons-variants.html 1A).
 */
export function AddFilterMenu({ active, onPick, extraGroups = [], pinned = [], onTogglePin, recent = [], filterCount = 0, onOpenBuilder }: {
  active: string[];
  onPick: (key: string) => void;
  extraGroups?: { title: string; fields: FilterField[] }[];
  pinned?: string[];
  onTogglePin?: (key: string) => void;
  recent?: { label: string; onPick: () => void }[];
  /** How many filters apply now, for the line that opens the builder. */
  filterCount?: number;
  /** Opens the "Filters" builder - a second way to it, from here (variant 2D). */
  onOpenBuilder?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Going on to the builder: its window takes the focus, not this trigger.
  const toBuilder = useRef(false);
  const q = query.trim().toLowerCase();
  const groups = [...FILTER_FIELD_GROUPS, ...extraGroups]
    .map((g) => ({ ...g, fields: g.fields.filter((f) => !q || f.label.toLowerCase().includes(q) || f.key.includes(q)) }))
    .filter((g) => g.fields.length);
  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          data-add-filter
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-cta px-3 py-1 text-xs font-medium text-cta-foreground shadow-xs hover:bg-cta/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
        >
          <FunnelPlus className="size-3.5" aria-hidden /> Add filter
        </button>
      </PopoverTrigger>
      {/* Never taller than the room on screen: on a short window the bottom
          groups were cut off and out of reach. The search stays, the list scrolls. */}
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[min(16rem,calc(100vw-1rem))] flex-col gap-1 p-2"
        onCloseAutoFocus={(e) => {
          if (toBuilder.current) e.preventDefault();
          toBuilder.current = false;
        }}
      >
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a field…"
          aria-label="Find a field"
          className="mb-1 h-8 w-full shrink-0 rounded-md border border-border bg-transparent px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {!q && recent.length ? (
            <div role="group" aria-label="Recent">
              <p className="px-1 pt-1 pb-0.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">Recent</p>
              {recent.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  title={r.label}
                  onClick={() => {
                    close();
                    r.onPick();
                  }}
                  className="flex w-full cursor-pointer items-center gap-1.5 rounded px-1 py-1 text-left text-xs hover:bg-muted"
                >
                  <History className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="truncate">{r.label}</span>
                </button>
              ))}
            </div>
          ) : null}
          {groups.map((g) => (
            <div key={g.title} role="group" aria-label={g.title}>
              <p className="px-1 pt-1 pb-0.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{g.title}</p>
              {g.fields.map((f) => {
                const used = active.includes(f.key);
                const isPinned = pinned.includes(f.key);
                return (
                  <div key={f.key} className="group/item flex items-center rounded hover:bg-muted">
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        onPick(f.key);
                      }}
                      title={used ? "In use - opens its filter" : undefined}
                      className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-1 px-1 py-1 text-left text-sm"
                    >
                      <span className="truncate">{f.label}</span>
                      {used ? <Check className="size-3.5 shrink-0 text-accent-foreground" aria-label="in use" /> : null}
                    </button>
                    {onTogglePin && PINNABLE.has(f.key) ? (
                      <button
                        type="button"
                        onClick={() => onTogglePin(f.key)}
                        aria-pressed={isPinned}
                        aria-label={isPinned ? `Unpin ${f.label} from the bar` : `Pin ${f.label} to the bar`}
                        title={isPinned ? "Pinned to the bar - click to unpin" : `Pin to the bar (up to ${PINNED_MAX})`}
                        disabled={!isPinned && pinned.length >= PINNED_MAX}
                        className={cn(
                          "inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded disabled:cursor-default disabled:opacity-30",
                          isPinned
                            ? "text-accent-foreground"
                            : "text-muted-foreground opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100",
                        )}
                      >
                        <Pin className="size-3.5" fill={isPinned ? "currentColor" : "none"} aria-hidden />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
          {!groups.length ? <p className="px-1 py-1 text-xs text-muted-foreground">No such field</p> : null}
        </div>
        {onOpenBuilder ? (
          <button
            type="button"
            onClick={() => {
              toBuilder.current = true;
              close();
              onOpenBuilder();
            }}
            className="shrink-0 cursor-pointer rounded border-t border-border px-1 pt-2 pb-1 text-left text-xs text-accent-foreground hover:bg-muted"
          >
            {filterCount ? `Edit all ${filterCount} ${filterCount === 1 ? "filter" : "filters"} as a list` : "Add several at once"} →
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
